/**
 * PostMessage Service for Native App Communication
 * Handles sending and receiving messages between MFE and native app
 */

export interface PostMessageData {
  type: string;
  payload?: any;
  source?: string;
  timestamp?: string;
}

export class PostMessageService {
  private static instance: PostMessageService;
  private listeners: Array<(event: MessageEvent<PostMessageData>) => void> = [];

  private constructor() {
    this.setupMessageListener();
  }

  public static getInstance(): PostMessageService {
    if (!PostMessageService.instance) {
      PostMessageService.instance = new PostMessageService();
    }
    return PostMessageService.instance;
  }

  /**
   * Send a message to the native app
   */
  public sendToNativeApp(message: Omit<PostMessageData, 'source' | 'timestamp'>): void {
    const messageData: PostMessageData = {
      source: 'menu-mfe',
      timestamp: new Date().toISOString(),
      ...message,
    };

    console.log('📤 Sending postMessage to native app:', messageData);

    // Priority 1: iOS webkit.messageHandlers (preferred for iOS)
    if ((window as any).webkit?.messageHandlers?.nativeHandler) {
      console.log('📤 Using webkit.messageHandlers.nativeHandler (iOS)');
      (window as any).webkit.messageHandlers.nativeHandler.postMessage(messageData);
      return;
    }

    // Priority 2: Send to parent window (if in iframe)
    if (window.parent && window.parent !== window) {
      console.log('📤 Using window.parent.postMessage');
      window.parent.postMessage(messageData, '*');
      return;
    }

    // Priority 3: Send to window.opener
    if (window.opener) {
      console.log('📤 Using window.opener.postMessage');
      window.opener.postMessage(messageData, '*');
      return;
    }

    console.warn('⚠️ No native bridge found to send message to');
  }

  /**
   * Add a listener for incoming messages
   */
  public addMessageListener(listener: (event: MessageEvent<PostMessageData>) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Remove a message listener
   */
  public removeMessageListener(listener: (event: MessageEvent<PostMessageData>) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Safe logging utility to handle circular references
   */
  private safeLog(label: string, data: any): void {
    try {
      // Try to stringify first to catch circular references
      JSON.stringify(data);
      console.log(label, data);
    } catch (error) {
      // If circular reference, log a safe representation
      console.log(label, {
        type: typeof data,
        constructor: data?.constructor?.name,
        keys: data && typeof data === 'object' ? Object.keys(data) : 'N/A',
        stringified: String(data),
        hasCircularRef: true,
      });
    }
  }

  /**
   * Setup the main message listener
   */
  private setupMessageListener(): void {
    const handleMessage = (event: MessageEvent<PostMessageData>) => {
      // Check if this is a lifecycle event
      const isLifecycleEvent = event.data?.type === 'LIFECYCLE_EVENT';

      if (isLifecycleEvent) {
        // Special formatting for lifecycle events
        console.log('LIFECYCLE EVENT  (via PostMessage) >>', JSON.stringify(event.data));
        console.log('LIFECYCLE EVENT >> Origin:', event.origin);
        console.log('LIFECYCLE EVENT >> Data:', JSON.stringify(event.data));
        this.safeLog('LIFECYCLE EVENT >> Source:', event.source);
        console.log('LIFECYCLE EVENT >>');
      } else {
        // Regular formatting for other events
        console.group('🔔 PostMessage from Native App');
        console.log('Origin:', event.origin);
        console.log('Data:', JSON.stringify(event.data));
        this.safeLog('Source:', event.source);
        console.log('Timestamp:', new Date().toISOString());
        console.groupEnd();
      }

      // Notify all listeners
      this.listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error('Error in postMessage listener:', error);
        }
      });
    };

    window.addEventListener('message', handleMessage);
  }

  /**
   * Send common message types
   */
  public sendMfeReady(userId: string): void {
    this.sendToNativeApp({
      type: 'MFE_READY',
      payload: { userId, timestamp: new Date().toISOString() },
    });
  }

  public sendNavigationRequest(target: string, userId: string, state?: any): void {
    this.sendToNativeApp({
      type: 'NAVIGATION_REQUEST',
      payload: {
        target,
        userId,
        timestamp: new Date().toISOString(),
        state,
      },
    });
  }
}

// Export singleton instance
export const postMessageService = PostMessageService.getInstance();

// Make it globally available for easy access
declare global {
  interface Window {
    sendMessageToNativeApp?: (message: Omit<PostMessageData, 'source' | 'timestamp'>) => void;
    postMessageService?: PostMessageService;
  }
}
